import type { RuntimeId } from './types.js';

export interface InstalledModel {
  name: string;
  sizeGb: number;
  modifiedAt: string;
  family?: string;
}

export interface RuntimeProbe {
  runtime: RuntimeId;
  reachable: boolean;
  url: string;
  installedModels: InstalledModel[];
  error?: string;
  version?: string;
}

const DEFAULT_URL_OLLAMA = 'http://127.0.0.1:11434';
const DEFAULT_URL_LM_STUDIO = 'http://127.0.0.1:1234';

async function fetchWithTimeout(url: string, timeoutMs = 900): Promise<Response | null> {
  if (typeof fetch === 'undefined') return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function probeOllama(baseUrl = DEFAULT_URL_OLLAMA): Promise<RuntimeProbe> {
  const tagsResp = await fetchWithTimeout(`${baseUrl}/api/tags`);
  if (!tagsResp || !tagsResp.ok) {
    return {
      runtime: 'ollama',
      reachable: false,
      url: baseUrl,
      installedModels: [],
      error: tagsResp ? `HTTP ${tagsResp.status}` : 'Not reachable — Ollama not running, or CORS blocked',
    };
  }

  const payload = (await tagsResp.json().catch(() => ({}))) as {
    models?: Array<{ name?: string; size?: number; modified_at?: string; details?: { family?: string } }>;
  };

  const installedModels: InstalledModel[] = (payload.models ?? []).map((m) => ({
    name: String(m.name ?? 'unknown'),
    sizeGb: m.size ? Math.round((m.size / 1_000_000_000) * 10) / 10 : 0,
    modifiedAt: String(m.modified_at ?? ''),
    family: m.details?.family,
  }));

  const versionResp = await fetchWithTimeout(`${baseUrl}/api/version`);
  let version: string | undefined;
  if (versionResp?.ok) {
    const v = (await versionResp.json().catch(() => ({}))) as { version?: string };
    version = v.version;
  }

  return {
    runtime: 'ollama',
    reachable: true,
    url: baseUrl,
    installedModels,
    version,
  };
}

export async function probeLmStudio(baseUrl = DEFAULT_URL_LM_STUDIO): Promise<RuntimeProbe> {
  const resp = await fetchWithTimeout(`${baseUrl}/v1/models`);
  if (!resp || !resp.ok) {
    return {
      runtime: 'lm-studio',
      reachable: false,
      url: baseUrl,
      installedModels: [],
      error: resp ? `HTTP ${resp.status}` : 'Not reachable — LM Studio server not running, or CORS blocked',
    };
  }
  const payload = (await resp.json().catch(() => ({}))) as { data?: Array<{ id?: string }> };
  const installedModels: InstalledModel[] = (payload.data ?? []).map((m) => ({
    name: String(m.id ?? 'unknown'),
    sizeGb: 0,
    modifiedAt: '',
  }));
  return { runtime: 'lm-studio', reachable: true, url: baseUrl, installedModels };
}

export async function probeAllRuntimes(): Promise<RuntimeProbe[]> {
  return Promise.all([probeOllama(), probeLmStudio()]);
}
